// src/analytics/utm.ts
//
// Captures UTM attribution params once on load and keeps them available for the
// whole session. Read from the URL on first import, then persisted to
// sessionStorage so they survive in-SPA navigation (react-router pushes that
// drop the query string) without leaking across browser sessions/tabs.
//
// Every read is defensive: a failure here must never bubble up into logEvent.

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const;

const STORAGE_KEY = "gather_utm";

type UtmParams = Record<string, string>;

// Read UTM params from the current URL. Only keys actually present (and
// non-empty) are returned.
const readFromUrl = (): UtmParams => {
  const out: UtmParams = {};
  try {
    const search = new URLSearchParams(window.location.search);
    for (const key of UTM_KEYS) {
      const value = search.get(key);
      if (value) out[key] = value;
    }
  } catch {
    // Malformed URL / no window — just return whatever we have.
  }
  return out;
};

const readFromStorage = (): UtmParams => {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};
    const out: UtmParams = {};
    for (const key of UTM_KEYS) {
      const value = (parsed as Record<string, unknown>)[key];
      if (typeof value === "string" && value) out[key] = value;
    }
    return out;
  } catch {
    return {};
  }
};

const persist = (params: UtmParams): void => {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(params));
  } catch {
    // Private mode / storage disabled — fall back to in-memory cache only.
  }
};

// Resolve once at module load: prefer fresh URL params, otherwise reuse what was
// stored earlier this session. If the URL has UTMs, they refresh the store.
const resolve = (): UtmParams => {
  const fromUrl = readFromUrl();
  if (Object.keys(fromUrl).length > 0) {
    persist(fromUrl);
    return fromUrl;
  }
  return readFromStorage();
};

let cached: UtmParams = {};
try {
  cached = resolve();
} catch {
  cached = {};
}

// Returns only the UTM params present for this session (empty for organic).
export const getUtmParams = (): UtmParams => ({ ...cached });
